import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import SignUpDialog from "@/components/SignUpDialog";
import { useAuth } from "@/hooks/useAuth";
import heroImg from "@/assets/hero-community-hands.jpg";
import { useSiteContent } from "@/hooks/useSiteContent";
import { getBlock } from "@/lib/siteContent";
import { useSeason } from "@/components/seasonal/SeasonalThemeProvider";

const CommunityWelcomeHero = () => {
  const [signUpOpen, setSignUpOpen] = useState(false);
  const [idea, setIdea] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();
  const { blocks } = useSiteContent("home");
  const { season } = useSeason();

  const eyebrowLeft = getBlock(blocks, "hero_eyebrow_left", "Alabama");
  const eyebrowRight = getBlock(blocks, "hero_eyebrow_right", "Civic Infrastructure");
  const headline = getBlock(
    blocks,
    "hero_headline",
    "Welcome to Alabama's ideascape.",
  );
  const subheadline = getBlock(
    blocks,
    "hero_subheadline",
    "We're a Strategic Communications firm for small organizations and businesses. We support the goals and ideas that drive our communities in Alabama forward.",
  );

  const submit = () => {
    if (!user) return setSignUpOpen(true);
    const q = idea.trim()
      ? `?new=1&title=${encodeURIComponent(idea.trim())}`
      : "?new=1";
    navigate(`/ideas${q}`);
  };

  return (
    <>
      <SignUpDialog open={signUpOpen} onOpenChange={setSignUpOpen} />
      <section className="relative bg-hero overflow-hidden pt-20">
        <div className="absolute inset-0">
          <img
            src={heroImg}
            alt="Two pairs of hands clasped together over an Alabama map"
            className="w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-[hsl(140_12%_14%_/_0.92)] md:bg-[hsl(140_12%_14%_/_0.86)]" />
        </div>

        <div className="container mx-auto px-6 relative z-10 py-16 md:py-28 min-h-[60vh] md:min-h-[70vh] flex flex-col items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-3 mb-6 text-hero-muted text-xs font-semibold tracking-[0.2em] uppercase"
          >
            <span>{eyebrowLeft}</span>
            <span className="w-1 h-1 rounded-full bg-current opacity-60" />
            <span>{eyebrowRight}</span>
          </motion.div>

          {season && season.badge_text && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.08 }}
              className="inline-flex items-center gap-2 mb-6 px-3 py-1 rounded-full text-[11px] font-semibold tracking-wide border border-white/15 bg-white/5 backdrop-blur text-hero-fg"
            >
              {season.badge_emoji && <span aria-hidden>{season.badge_emoji}</span>}
              <span>{season.badge_text}</span>
            </motion.div>
          )}

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-display text-[2.5rem] sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight text-hero-fg mb-5 max-w-4xl"
          >
            {headline}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18 }}
            className="text-hero-muted text-sm md:text-lg max-w-2xl mb-8 leading-relaxed"
          >
            {subheadline}
          </motion.p>

          <motion.form
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.24 }}
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
            className="w-full max-w-2xl"
          >
            <label htmlFor="hero-idea" className="sr-only">
              Share an idea for your community
            </label>
            <div className="flex flex-col sm:flex-row items-stretch gap-2 rounded-2xl sm:rounded-full border border-white/20 bg-white/10 backdrop-blur p-2 focus-within:border-accent/70 transition-colors">
              <div className="flex items-center gap-2 flex-1 px-3">
                <Sparkles className="h-4 w-4 text-accent shrink-0" aria-hidden />
                <input
                  id="hero-idea"
                  value={idea}
                  onChange={(e) => setIdea(e.target.value)}
                  placeholder="Share an idea for your community…"
                  maxLength={200}
                  className="w-full bg-transparent py-3 text-hero-fg placeholder:text-hero-muted text-sm md:text-base outline-none"
                />
              </div>
              <Button
                type="submit"
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 font-display font-semibold rounded-full px-6 h-12 shadow-glow shrink-0"
              >
                Share it <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </motion.form>

          <motion.a
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.32 }}
            href="#anchor-case-study"
            className="mt-8 inline-flex items-center gap-2 text-hero-fg/90 hover:text-hero-fg text-sm font-semibold"
          >
            <ArrowDown className="h-4 w-4" />
            See what we&apos;re working on
          </motion.a>
        </div>
      </section>
    </>
  );
};

export default CommunityWelcomeHero;